/**
 * batch-68 controle (FO-342 verloningscontrole + FO-343 facturatiecontrole) -
 * gedeelde contracten van de generieke controlekern.
 *
 * Eén bron voor: de enums (gespiegeld uit schema.prisma), de regel-registry
 * (sleutel, nummer, naam, standaard-ernst, drempels), de request-schema's die de
 * API `.safeParse`t en de backoffice vóór verzenden gebruikt (inline veldfouten),
 * en de DTO's van het controlescherm.
 *
 * De controle is in UREN (verloning) resp. euro's (facturatie, fase C). Alle
 * getallen in de DTO's zijn al op twee decimalen afgerond door de API (die rekent
 * in Decimal en rondt pas op het laatste moment af).
 */
import { z } from 'zod'
import { queryBoolean } from './zod-query'

// ─── Enums (spiegel van schema.prisma, sectie batch-68 controle) ──────────────

export const CONTROLE_DOMEINEN = ['VERLONING', 'FACTURATIE'] as const
export type ControleDomein = (typeof CONTROLE_DOMEINEN)[number]

/** Wie een controle-subject is: een medewerker (VERLONING) of een klant (FACTURATIE). */
export const CONTROLE_SUBJECT_TYPES = ['EMPLOYEE', 'CLIENT'] as const
export type ControleSubjectType = (typeof CONTROLE_SUBJECT_TYPES)[number]

export const CONTROLE_ERNSTEN = ['OK', 'INFO', 'ORANJE', 'ROOD'] as const
export type ControleErnst = (typeof CONTROLE_ERNSTEN)[number]

/** Tekstlabel bij elke ernst: kleur is nooit de enige drager (FO-342 §5). */
export const CONTROLE_ERNST_LABELS: Record<ControleErnst, string> = {
  OK: 'In orde',
  INFO: 'Ter info',
  ORANJE: 'Controleren',
  ROOD: 'Blokkerend',
}

/** Zwaarte-volgorde (hoger = zwaarder) voor sortering en "de zwaarste wint". */
export const CONTROLE_ERNST_GEWICHT: Record<ControleErnst, number> = {
  OK: 0,
  INFO: 1,
  ORANJE: 2,
  ROOD: 3,
}

/** De zwaarste ernst uit een lijst (OK bij een lege lijst). */
export function zwaarsteErnst(ernsten: readonly ControleErnst[]): ControleErnst {
  let max: ControleErnst = 'OK'
  for (const e of ernsten) if (CONTROLE_ERNST_GEWICHT[e] > CONTROLE_ERNST_GEWICHT[max]) max = e
  return max
}

export const CONTROLE_BEVINDING_STATUSSEN = [
  'OPEN',
  'GECONTROLEERD',
  'GEACCEPTEERD',
  'OPGELOST',
  'HEROPEND',
] as const
export type ControleBevindingStatus = (typeof CONTROLE_BEVINDING_STATUSSEN)[number]

export const CONTROLE_BEVINDING_STATUS_LABELS: Record<ControleBevindingStatus, string> = {
  OPEN: 'Open',
  GECONTROLEERD: 'Gecontroleerd',
  GEACCEPTEERD: 'Geaccepteerd',
  OPGELOST: 'Opgelost',
  HEROPEND: 'Heropend (gewijzigd na controle)',
}

/** Statussen die nog aandacht vragen (tellen als "open"). */
export const CONTROLE_OPEN_STATUSSEN: readonly ControleBevindingStatus[] = ['OPEN', 'HEROPEND']

export function isOpenBevindingStatus(status: ControleBevindingStatus): boolean {
  return status === 'OPEN' || status === 'HEROPEND'
}

export const CONTROLE_ACTIE_TYPES = [
  'GECONTROLEERD',
  'GEACCEPTEERD',
  'OPMERKING',
  'CORRECTIE',
  'HEROPEND',
  'AKKOORD',
  'AKKOORD_HEROPEND',
  'VRIJGEGEVEN',
  'UITGESTELD',
  'VRIJGAVE_INGETROKKEN',
  'INSTELLING_GEWIJZIGD',
] as const
export type ControleActieType = (typeof CONTROLE_ACTIE_TYPES)[number]

export const CONTROLE_ACTIE_LABELS: Record<ControleActieType, string> = {
  GECONTROLEERD: 'Gemarkeerd als gecontroleerd',
  GEACCEPTEERD: 'Bewust geaccepteerd',
  OPMERKING: 'Opmerking',
  CORRECTIE: 'Bron gecorrigeerd (opgelost)',
  HEROPEND: 'Heropend: gewijzigd na controle',
  AKKOORD: 'Akkoord gegeven',
  AKKOORD_HEROPEND: 'Akkoord heropend',
  VRIJGEGEVEN: 'Vrijgegeven voor facturatie',
  UITGESTELD: 'Uitgesteld naar de volgende ronde',
  VRIJGAVE_INGETROKKEN: 'Vrijgave ingetrokken',
  INSTELLING_GEWIJZIGD: 'Instellingen gewijzigd',
}

export const CONTROLE_RUN_STATUSSEN = ['BEREKENEN', 'ACTUEEL', 'VEROUDERD', 'FOUT'] as const
export type ControleRunStatus = (typeof CONTROLE_RUN_STATUSSEN)[number]

export const CONTROLE_AKKOORD_STATUSSEN = ['ACTIEF', 'VERVANGEN'] as const
export type ControleAkkoordStatus = (typeof CONTROLE_AKKOORD_STATUSSEN)[number]

/** De minimale lengte van een reden bij accepteren/heropenen (FO-342 §3.9). */
export const CONTROLE_REDEN_MIN = 10

// ─── Regel-registry (FO-342 §3.7) ─────────────────────────────────────────────

export const VERLONING_REGEL_KEYS = [
  'ONTBREKENDE_UREN',
  'DUBBELE_UREN',
  'PLANNING_WIJKT_AF',
  'NORMUREN_NIET_BEHAALD',
  'ONVERWACHT_VEEL_UREN',
  'TVT_NORM',
  'TVT_WERK_NIET_ACTIEF',
  'TVT_REIS_NIET_ACTIEF',
  'TVT_OVERUREN_INSTELLING',
  'PAUZE',
  'SALDO_MUTATIE_ONVERWACHT',
  'HANDMATIGE_CORRECTIE',
  'SALDO_SLUIT_NIET',
  'UREN_NIET_GEKOPPELD',
  'TVT_BOVEN_PLAFOND',
  'CONTRACTWIJZIGING',
  'UIT_DIENST',
] as const
export type VerloningRegelKey = (typeof VERLONING_REGEL_KEYS)[number]

export interface ControleRegelVariant {
  /** Sleutel van de variant binnen de regel (bv. `DAG`, `WEEKSTAAT_OPEN`). */
  key: string
  label: string
  standaardErnst: ControleErnst
}

export interface ControleRegelDrempel {
  key: string
  label: string
  standaard: number
  eenheid: 'uur' | 'minuten' | 'procent'
  min: number
  max: number
}

export interface ControleRegelMeta {
  key: string
  /** Nummer uit de FO-tabel (1-17 resp. 1-23). */
  nummer: number
  /** Kort label voor de chip op het hoofdscherm ("Pauze (2)"). */
  kort: string
  naam: string
  /** Korte uitleg van de detectie (voor het instellingenscherm en de export). */
  detectie: string
  varianten: readonly ControleRegelVariant[]
  drempels: readonly ControleRegelDrempel[]
  /**
   * Integriteitscontrole: altijd aan en altijd blokkerend (FO-342: regel 2 en 13
   * "mogen nooit zwijgen"). Het instellingenscherm toont hem vergrendeld.
   */
  vergrendeld: boolean
}

export const VERLONING_REGELS: readonly ControleRegelMeta[] = [
  {
    key: 'ONTBREKENDE_UREN',
    kort: 'Uren ontbreken',
    nummer: 1,
    naam: 'Ontbrekende uren',
    detectie:
      'Een geplande werkbon-dag zonder registratie en zonder afwezigheid, een weekstaat in de maand die niet definitief is, of een medewerker zonder enige registratie of afwezigheid.',
    varianten: [
      { key: 'DAG', label: 'Geplande dag zonder registratie', standaardErnst: 'ORANJE' },
      { key: 'WEEKSTAAT_OPEN', label: 'Weekstaat niet definitief', standaardErnst: 'ROOD' },
      { key: 'GEEN_UREN', label: 'Geen uren en geen afwezigheid in de maand', standaardErnst: 'ROOD' },
    ],
    drempels: [],
    vergrendeld: false,
  },
  {
    key: 'DUBBELE_UREN',
    kort: 'Dubbele uren',
    nummer: 2,
    naam: 'Dubbele uren',
    detectie:
      'Overlappende tijdvakken van dezelfde medewerker op dezelfde dag, of identieke registraties op twee werkbonnen.',
    varianten: [{ key: 'OVERLAP', label: 'Overlappende of dubbele uren', standaardErnst: 'ROOD' }],
    drempels: [],
    vergrendeld: true,
  },
  {
    key: 'PLANNING_WIJKT_AF',
    kort: 'Planning wijkt af',
    nummer: 3,
    naam: 'Planning wijkt af van registratie',
    detectie: 'Het verschil tussen gepland en geregistreerd is per dag groter dan de drempel.',
    varianten: [{ key: 'AFWIJKING', label: 'Afwijking planning en registratie', standaardErnst: 'ORANJE' }],
    drempels: [{ key: 'afwijkingUren', label: 'Afwijking per dag', standaard: 1, eenheid: 'uur', min: 0, max: 24 }],
    vergrendeld: false,
  },
  {
    key: 'NORMUREN_NIET_BEHAALD',
    kort: 'Normuren niet behaald',
    nummer: 4,
    naam: 'Normuren niet behaald',
    detectie:
      'Gewerkt plus doorbetaalde afwezigheid plus de reisuren-aanvulling blijft onder de contracturen van de maand (FO-318). Toont het tekort en of er een min-urenverzoek loopt.',
    varianten: [{ key: 'TEKORT', label: 'Tekort op de contracturen', standaardErnst: 'ORANJE' }],
    drempels: [],
    vergrendeld: false,
  },
  {
    key: 'ONVERWACHT_VEEL_UREN',
    kort: 'Veel uren',
    nummer: 5,
    naam: 'Onverwacht veel uren',
    detectie: 'Meer dan de drempel per dag, per week of als percentage van de contracturen per maand.',
    varianten: [{ key: 'VEEL', label: 'Ongebruikelijk veel uren', standaardErnst: 'ORANJE' }],
    drempels: [
      { key: 'maxPerDag', label: 'Maximum per dag', standaard: 12, eenheid: 'uur', min: 1, max: 24 },
      { key: 'maxPerWeek', label: 'Maximum per week', standaard: 60, eenheid: 'uur', min: 1, max: 168 },
      { key: 'maxPctContract', label: 'Maximum per maand', standaard: 140, eenheid: 'procent', min: 100, max: 500 },
    ],
    vergrendeld: false,
  },
  {
    key: 'TVT_NORM',
    kort: 'TVT-norm',
    nummer: 6,
    naam: 'TVT-opbouw niet behaald of ongebruikelijk hoog',
    detectie:
      'Per week: de werkelijke TVT-opbouw tegenover de verwachting uit het gemiddelde van de vorige maanden (normale weken), pro-rata naar werkbare dagen en afgetopt op het plafond. Nooit blokkerend.',
    varianten: [
      { key: 'TE_LAAG', label: 'Opbouw lager dan verwacht', standaardErnst: 'ORANJE' },
      { key: 'TE_HOOG', label: 'Opbouw ongebruikelijk hoog', standaardErnst: 'ORANJE' },
    ],
    drempels: [],
    vergrendeld: false,
  },
  {
    key: 'TVT_WERK_NIET_ACTIEF',
    kort: 'TVT werk niet actief',
    nummer: 7,
    naam: 'TVT werk geboekt terwijl niet actief',
    detectie: 'TVT-opbouw werkuren in een week waarin TVT werk of ATV-opbouw op de maandag uit stond.',
    varianten: [{ key: 'NIET_ACTIEF', label: 'TVT werk tegen de instelling in', standaardErnst: 'ROOD' }],
    drempels: [],
    vergrendeld: false,
  },
  {
    key: 'TVT_REIS_NIET_ACTIEF',
    kort: 'TVT reis niet actief',
    nummer: 8,
    naam: 'TVT reis geboekt terwijl niet actief',
    detectie: 'TVT-opbouw reisuren op een dag waarop TVT reis of ATV-opbouw uit stond.',
    varianten: [{ key: 'NIET_ACTIEF', label: 'TVT reis tegen de instelling in', standaardErnst: 'ROOD' }],
    drempels: [],
    vergrendeld: false,
  },
  {
    key: 'TVT_OVERUREN_INSTELLING',
    kort: 'TVT en overuren',
    nummer: 9,
    naam: 'TVT-overuren tegen de instelling in',
    detectie:
      'Week-net overwerk terwijl TVT werk aan stond, maar de TVT-opbouw wijkt af van overwerk x opbouwpercentage (dubbel: opbouw en uitbetaling, of opbouw die ontbreekt).',
    varianten: [{ key: 'AFWIJKING', label: 'Opbouw en verloning lopen niet gelijk', standaardErnst: 'ROOD' }],
    drempels: [],
    vergrendeld: false,
  },
  {
    key: 'PAUZE',
    kort: 'Pauze',
    nummer: 10,
    naam: 'Onlogische of ontbrekende pauze',
    detectie:
      'Netto boven de eerste ATW-grens met te weinig pauze, boven de tweede grens met te weinig pauze, een pauze van meer dan de helft van de dienst, of een pauze die afwijkt van de dienst-standaard. Een bewust "geen pauze"-vinkje telt als verklaard.',
    varianten: [
      { key: 'TE_KORT', label: 'Te korte of ontbrekende pauze', standaardErnst: 'ORANJE' },
      { key: 'TE_LANG', label: 'Pauze langer dan de helft van de dienst', standaardErnst: 'ORANJE' },
      { key: 'AFWIJKEND_VAN_STANDAARD', label: 'Pauze afwijkend van de dienst-standaard', standaardErnst: 'INFO' },
    ],
    drempels: [
      { key: 'grens1Uren', label: 'Eerste grens (netto)', standaard: 5.5, eenheid: 'uur', min: 0, max: 24 },
      { key: 'grens1Minuten', label: 'Minimale pauze boven de eerste grens', standaard: 30, eenheid: 'minuten', min: 0, max: 240 },
      { key: 'grens2Uren', label: 'Tweede grens (netto)', standaard: 10, eenheid: 'uur', min: 0, max: 24 },
      { key: 'grens2Minuten', label: 'Minimale pauze boven de tweede grens', standaard: 45, eenheid: 'minuten', min: 0, max: 240 },
      { key: 'maxPctDienst', label: 'Maximale pauze als deel van de dienst', standaard: 50, eenheid: 'procent', min: 1, max: 100 },
    ],
    vergrendeld: false,
  },
  {
    key: 'SALDO_MUTATIE_ONVERWACHT',
    kort: 'Saldo-mutatie',
    nummer: 11,
    naam: 'Negatieve of onverwachte saldo-mutaties',
    detectie:
      'Een afname of correctie die een emmer onder 0 brengt, verval buiten de vervaldatum, opbouw met een negatieve waarde, of een mutatie met een datum buiten de maand die in de maand is aangemaakt (terugwerkend).',
    varianten: [
      { key: 'SALDO_NEGATIEF', label: 'Saldo onder 0', standaardErnst: 'ROOD' },
      { key: 'ONVERWACHT', label: 'Onverwachte mutatie', standaardErnst: 'ORANJE' },
    ],
    drempels: [],
    vergrendeld: false,
  },
  {
    key: 'HANDMATIGE_CORRECTIE',
    kort: 'Handmatige correctie',
    nummer: 12,
    naam: 'Handmatige correcties',
    detectie:
      'Een payroll-correctie in de maand, een handmatige saldo-correctie (ledger), een overschreven reistijd (goedgekeurde reisafwijking) of een wijziging aan een weekstaat na het afsluiten. Elk moet gecontroleerd worden.',
    varianten: [{ key: 'CORRECTIE', label: 'Handmatige correctie', standaardErnst: 'ORANJE' }],
    drempels: [],
    vergrendeld: false,
  },
  {
    key: 'SALDO_SLUIT_NIET',
    kort: 'Saldo sluit niet',
    nummer: 13,
    naam: 'Onverklaarbaar saldo-verschil',
    detectie:
      'Per emmer: beginsaldo plus de mutaties is niet gelijk aan het eindsaldo (meer dan 0,01). Integriteitscontrole op de ledger.',
    varianten: [{ key: 'SLUIT_NIET', label: 'Ledger sluit niet', standaardErnst: 'ROOD' }],
    drempels: [],
    vergrendeld: true,
  },
  {
    key: 'UREN_NIET_GEKOPPELD',
    kort: 'Niet gekoppeld',
    nummer: 14,
    naam: 'Uren niet gekoppeld',
    detectie:
      'Uren op een werkbon waarvan de klant niet (meer) bestaat en die niet intern is, of uitbetaalde reisuren zonder werkbon-dag.',
    varianten: [{ key: 'NIET_GEKOPPELD', label: 'Uren zonder klant of werkbon-dag', standaardErnst: 'ROOD' }],
    drempels: [],
    vergrendeld: false,
  },
  {
    key: 'TVT_BOVEN_PLAFOND',
    kort: 'TVT boven plafond',
    nummer: 15,
    naam: 'TVT-saldo boven plafond',
    detectie:
      'Het eindsaldo van een week of de maand ligt boven het maximum per emmer (1 werkweek = 38 of 40 uur, bij parttime de contracturen). Alleen een signaal: het plafond wordt nog niet afgedwongen.',
    varianten: [{ key: 'BOVEN_PLAFOND', label: 'Saldo boven het maximum', standaardErnst: 'ORANJE' }],
    drempels: [],
    vergrendeld: false,
  },
  {
    key: 'CONTRACTWIJZIGING',
    // Een wissel per de 1e is een schone maandstart (geen pro-rata), geen wissel "in" de maand.
    kort: 'Contractwijziging',
    nummer: 16,
    naam: 'Contractwijziging in de maand',
    detectie:
      'Een contractmutatie met een ingangsdatum na de 1e van de maand (uren, ATV, TVT-vinkjes, percentage). Controleer de pro-rata; een wissel per de 1e is een schone maandstart.',
    varianten: [{ key: 'WIJZIGING', label: 'Contractwijziging', standaardErnst: 'ORANJE' }],
    drempels: [],
    vergrendeld: false,
  },
  {
    key: 'UIT_DIENST',
    kort: 'Uit dienst',
    nummer: 17,
    naam: 'Uit dienst in de maand',
    detectie: 'De einddatum valt in de maand: wordt een TVT- of ATV-eindsaldo uitbetaald of vervalt het?',
    varianten: [{ key: 'UIT_DIENST', label: 'Uit dienst met restsaldo', standaardErnst: 'ORANJE' }],
    drempels: [],
    vergrendeld: false,
  },
]

const REGEL_META_INDEX = new Map(VERLONING_REGELS.map((r) => [r.key, r] as const))

export function verloningRegelMeta(key: string): ControleRegelMeta | undefined {
  return REGEL_META_INDEX.get(key)
}

// ─── Instellingen ─────────────────────────────────────────────────────────────

export const controleRegelConfigSchema = z.object({
  aan: z.boolean(),
  /** Per variant een andere ernst dan de standaard. */
  ernst: z.record(z.string(), z.enum(CONTROLE_ERNSTEN)).optional(),
  /** Per drempel-sleutel een andere waarde dan de standaard. */
  drempels: z.record(z.string(), z.number().finite()).optional(),
})
export type ControleRegelConfig = z.infer<typeof controleRegelConfigSchema>

const ymd = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Kies een geldige datum')

/** PUT /payroll/controle/instellingen - gedeeld met het instellingenformulier. */
export const verloningControleInstellingenSchema = z
  .object({
    regels: z.record(z.string(), controleRegelConfigSchema),
    referentieMaanden: z.number().int().min(1, 'Minimaal 1 maand').max(6, 'Maximaal 6 maanden'),
    minNormaleWeken: z.number().int().min(1, 'Minimaal 1 week').max(26, 'Maximaal 26 weken'),
    tvtTolerantie: z.number().min(0, 'Niet negatief').max(10, 'Maximaal 10 uur'),
    gateActief: z.boolean(),
    gateVanaf: ymd.nullable(),
    herinneringWerkdag: z.number().int().min(1, 'Minimaal werkdag 1').max(15, 'Maximaal werkdag 15'),
  })
  .superRefine((v, ctx) => {
    for (const [key, cfg] of Object.entries(v.regels)) {
      const meta = verloningRegelMeta(key)
      if (!meta) {
        ctx.addIssue({ code: 'custom', path: ['regels', key], message: 'Onbekende regel' })
        continue
      }
      if (meta.vergrendeld && !cfg.aan) {
        ctx.addIssue({
          code: 'custom',
          path: ['regels', key, 'aan'],
          message: 'Deze integriteitscontrole kan niet uit',
        })
      }
      for (const [variant, ernst] of Object.entries(cfg.ernst ?? {})) {
        if (!meta.varianten.some((x) => x.key === variant)) {
          ctx.addIssue({ code: 'custom', path: ['regels', key, 'ernst', variant], message: 'Onbekende variant' })
        } else if (meta.vergrendeld && ernst !== 'ROOD') {
          ctx.addIssue({
            code: 'custom',
            path: ['regels', key, 'ernst', variant],
            message: 'Een integriteitscontrole blijft blokkerend',
          })
        }
      }
      for (const [drempel, waarde] of Object.entries(cfg.drempels ?? {})) {
        const d = meta.drempels.find((x) => x.key === drempel)
        if (!d) {
          ctx.addIssue({ code: 'custom', path: ['regels', key, 'drempels', drempel], message: 'Onbekende drempel' })
        } else if (waarde < d.min || waarde > d.max) {
          ctx.addIssue({
            code: 'custom',
            path: ['regels', key, 'drempels', drempel],
            message: `Kies een waarde tussen ${d.min} en ${d.max}`,
          })
        }
      }
    }
  })
export type VerloningControleInstellingenInput = z.infer<typeof verloningControleInstellingenSchema>

export interface ControleInstellingenDTO {
  domein: ControleDomein
  regels: Record<string, ControleRegelConfig>
  referentieMaanden: number
  minNormaleWeken: number
  tvtTolerantie: number
  gateActief: boolean
  gateVanaf: string | null
  herinneringWerkdag: number
  bijgewerktOp: string
  bijgewerktDoorNaam: string | null
  /** De regel-registry, zodat het formulier de standaarden kan tonen. */
  regelMeta: readonly ControleRegelMeta[]
}

// ─── Periode + lijst ──────────────────────────────────────────────────────────

const jaarSchema = z.coerce.number().int().min(2000).max(2100)
const maandSchema = z.coerce.number().int().min(1).max(12)

export const controlePeriodeQuerySchema = z.object({
  year: jaarSchema,
  month: maandSchema,
})
export type ControlePeriodeQuery = z.infer<typeof controlePeriodeQuerySchema>

export const CONTROLE_TEGEL_FILTERS = ['ALLE', 'GROEN', 'ORANJE', 'ROOD'] as const
export type ControleTegelFilter = (typeof CONTROLE_TEGEL_FILTERS)[number]

export const CONTROLE_LIJST_SORTERINGEN = ['STATUS', 'NAAM', 'GEWERKT', 'TVT', 'ATV'] as const
export type ControleLijstSortering = (typeof CONTROLE_LIJST_SORTERINGEN)[number]

export const verloningControleLijstQuerySchema = controlePeriodeQuerySchema.extend({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(500).default(100),
  zoek: z.string().trim().max(100).optional(),
  entityId: z.string().min(1).optional(),
  departmentId: z.string().min(1).optional(),
  tegel: z.enum(CONTROLE_TEGEL_FILTERS).default('ALLE'),
  regel: z.enum(VERLONING_REGEL_KEYS).optional(),
  /** Alleen medewerkers met nog open bevindingen. */
  alleenOpen: queryBoolean().optional(),
  sort: z.enum(CONTROLE_LIJST_SORTERINGEN).default('STATUS'),
})
export type VerloningControleLijstQuery = z.infer<typeof verloningControleLijstQuerySchema>

export const controleRunStartSchema = z.object({
  year: z.number().int().min(2000).max(2100),
  month: z.number().int().min(1).max(12),
  /** Alleen de verouderde medewerkers opnieuw berekenen. */
  alleenVerouderd: z.boolean().optional(),
})
export type ControleRunStartInput = z.infer<typeof controleRunStartSchema>

// ─── Afhandeling (FO-342 §3.9) ────────────────────────────────────────────────

export const CONTROLE_AFHANDEL_ACTIES = ['GECONTROLEERD', 'GEACCEPTEERD', 'OPMERKING'] as const
export type ControleAfhandelActie = (typeof CONTROLE_AFHANDEL_ACTIES)[number]

function redenRefine(
  v: { type: ControleAfhandelActie; reden?: string | undefined },
  ctx: z.RefinementCtx,
): void {
  const reden = v.reden?.trim() ?? ''
  if (v.type === 'GEACCEPTEERD' && reden.length < CONTROLE_REDEN_MIN) {
    ctx.addIssue({
      code: 'custom',
      path: ['reden'],
      message: `Geef een reden van minstens ${CONTROLE_REDEN_MIN} tekens`,
    })
  }
  if (v.type === 'OPMERKING' && reden.length === 0) {
    ctx.addIssue({ code: 'custom', path: ['reden'], message: 'Schrijf een opmerking' })
  }
}

/** POST /payroll/controle/bevindingen/:id/actie */
export const controleBevindingActieSchema = z
  .object({
    type: z.enum(CONTROLE_AFHANDEL_ACTIES),
    reden: z.string().max(2000, 'Maximaal 2000 tekens').optional(),
  })
  .superRefine(redenRefine)
export type ControleBevindingActieInput = z.infer<typeof controleBevindingActieSchema>

/** POST /payroll/controle/bevindingen/bulk-actie */
export const controleBulkActieSchema = z
  .object({
    ids: z.array(z.string().min(1)).min(1, 'Kies minstens één bevinding').max(500),
    type: z.enum(CONTROLE_AFHANDEL_ACTIES),
    reden: z.string().max(2000, 'Maximaal 2000 tekens').optional(),
  })
  .superRefine(redenRefine)
export type ControleBulkActieInput = z.infer<typeof controleBulkActieSchema>

/** POST /payroll/controle/medewerker/:employeeId/opmerking - notitie zonder bevinding. */
export const controleSubjectOpmerkingSchema = z.object({
  year: z.number().int().min(2000).max(2100),
  month: z.number().int().min(1).max(12),
  reden: z.string().trim().min(1, 'Schrijf een opmerking').max(2000, 'Maximaal 2000 tekens'),
})
export type ControleSubjectOpmerkingInput = z.infer<typeof controleSubjectOpmerkingSchema>

// ─── Akkoord (FO-342 §3.10) ───────────────────────────────────────────────────

export const controleAkkoordSchema = z.object({
  year: z.number().int().min(2000).max(2100),
  month: z.number().int().min(1).max(12),
  /** Bevestiging dat de openstaande oranje bevindingen bekend zijn. */
  bevestigOpenWaarschuwingen: z.boolean(),
})
export type ControleAkkoordInput = z.infer<typeof controleAkkoordSchema>

export const controleHeropenSchema = z.object({
  reden: z
    .string()
    .trim()
    .min(CONTROLE_REDEN_MIN, `Geef een reden van minstens ${CONTROLE_REDEN_MIN} tekens`)
    .max(2000, 'Maximaal 2000 tekens'),
})
export type ControleHeropenInput = z.infer<typeof controleHeropenSchema>

// ─── Uitzonderingen (FO-342 §11) ──────────────────────────────────────────────

/**
 * De regels waarvoor een per-medewerker-uitzondering mag: alleen regel 10 (pauze),
 * zoals FO-342 §11 hem beschrijft ("deze medewerker heeft structureel 45 min
 * pauze"). Een uitzondering op een andere regel kon ook rode regels stil uitzetten
 * (review fase A, belangrijk 14). Uitbreiden = bewuste keuze, hier.
 */
export const VERLONING_UITZONDERING_REGELS = ['PAUZE'] as const satisfies readonly VerloningRegelKey[]
export type VerloningUitzonderingRegel = (typeof VERLONING_UITZONDERING_REGELS)[number]

export function isUitzonderbareRegel(regelKey: string): regelKey is VerloningUitzonderingRegel {
  return (VERLONING_UITZONDERING_REGELS as readonly string[]).includes(regelKey)
}

export const verloningUitzonderingSchema = z
  .object({
    employeeId: z.string().min(1, 'Kies een medewerker'),
    regelKey: z.enum(VERLONING_UITZONDERING_REGELS, { message: 'Een uitzondering kan alleen op de pauzeregel' }),
    /**
     * Regel 10: verplicht { pauzeMinuten: N }, de vaste pauze van deze medewerker. Een
     * "pauzeregel uit" bestaat niet (meer): die zette ook de wettelijke ATW-controle uit
     * (review fase B). De uitzondering verklaart alleen de afwijking van de standaard.
     */
    waarde: z.record(z.string(), z.union([z.number().finite(), z.string().max(200), z.boolean()])),
    geldigVan: ymd,
    geldigTot: ymd.nullable(),
    reden: z
      .string()
      .trim()
      .min(CONTROLE_REDEN_MIN, `Geef een reden van minstens ${CONTROLE_REDEN_MIN} tekens`)
      .max(2000, 'Maximaal 2000 tekens'),
  })
  .superRefine((v, ctx) => {
    if (v.geldigTot && v.geldigTot < v.geldigVan) {
      ctx.addIssue({ code: 'custom', path: ['geldigTot'], message: 'Ligt vóór de begindatum' })
    }
    const pm = v.waarde.pauzeMinuten
    if (v.regelKey === 'PAUZE' && (typeof pm !== 'number' || pm < 0 || pm > 240)) {
      ctx.addIssue({ code: 'custom', path: ['waarde', 'pauzeMinuten'], message: 'Vul de vaste pauze in: 0 tot 240 minuten' })
    }
  })
export type VerloningUitzonderingInput = z.infer<typeof verloningUitzonderingSchema>

export interface ControleUitzonderingDTO {
  id: string
  subjectId: string
  subjectNaam: string
  regelKey: string
  regelNaam: string
  waarde: Record<string, number | string | boolean>
  geldigVan: string
  geldigTot: string | null
  reden: string
  aangemaaktDoorNaam: string | null
  aangemaaktOp: string
}

// ─── Herleiding (FO-342 §3.8) ─────────────────────────────────────────────────

export const VERLONING_HERLEIDING_VELDEN = [
  'GEWERKT',
  'TE_VERLONEN',
  'PAUZE',
  'TOESLAG',
  'OVERUREN',
  'REIS',
  'TVT_WERK',
  'TVT_REIS',
  'ATV',
  'CORRECTIES',
  'VERLOF',
  'ZIEKTE',
  'OVERIGE',
  'TVT_OPNAME',
] as const
export type VerloningHerleidingVeld = (typeof VERLONING_HERLEIDING_VELDEN)[number]

export const VERLONING_HERLEIDING_VELD_LABELS: Record<VerloningHerleidingVeld, string> = {
  GEWERKT: 'Gewerkte uren',
  TE_VERLONEN: 'Te verlonen uren',
  PAUZE: 'Pauzeaftrek',
  TOESLAG: 'Toeslaguren',
  OVERUREN: 'Overuren (week-net)',
  REIS: 'Uitbetaalde reisuren',
  TVT_WERK: 'TVT werk/overuren',
  TVT_REIS: 'TVT reis',
  ATV: 'ATV',
  CORRECTIES: 'Correcties',
  VERLOF: 'Verlof',
  ZIEKTE: 'Ziekte',
  OVERIGE: 'Overige uursoorten',
  TVT_OPNAME: 'TVT-opname (werk en reis)',
}

/** De velden met een vastgelegde afleiding per klantcel (review fase A, AC7). */
export const VERLONING_AFLEIDING_VELDEN = [
  'GEWERKT',
  'PAUZE',
  'TOESLAG',
  'CORRECTIES',
  'REIS',
  'TVT_WERK',
  'TVT_REIS',
  'ATV',
] as const
export type VerloningAfleidingVeld = (typeof VERLONING_AFLEIDING_VELDEN)[number]

/**
 * Eén bronregel van een klantcel: de som van de regels van een (veld, klant) is
 * de cel. Vastgelegd tijdens de berekening, zodat de drill-down geen tweede
 * berekening doet.
 */
export interface VerloningAfleidingRegelDTO {
  veld: VerloningAfleidingVeld
  /** Klantregel-sleutel (KLANT:<id>, INTERN, OPLEIDING, AFWEZIGHEID, NIET_GEKOPPELD). */
  klant: string
  datum: string | null
  /** De bijdrage (4 decimalen, exact; pas de som rondt af). */
  uren: number
  omschrijving: string
  bron: ControleBronLinkDTO | null
  toelichting: string | null
  /** Alleen TVT reis: telt mee in "naar verloning" (regulier) of niet (consignatie). */
  regulier?: boolean
}

export const verloningHerleidingQuerySchema = controlePeriodeQuerySchema.extend({
  employeeId: z.string().min(1),
  veld: z.enum(VERLONING_HERLEIDING_VELDEN),
  /** Beperk tot één ISO-week (`2026-W15`). */
  week: z
    .string()
    .regex(/^\d{4}-W\d{2}$/)
    .optional(),
  /**
   * Beperk tot één klantregel (klant-id of INTERN/OPLEIDING/AFWEZIGHEID/NIET_GEKOPPELD),
   * of `ALLE` = alle klantregels samen (de totaalregel van de klantverdeling).
   */
  klant: z.string().min(1).optional(),
  /** TVT/ATV: alleen de opbouw of alleen de opname (anders alle mutaties). */
  soort: z.enum(['OPBOUW', 'OPNAME']).optional(),
})
export type VerloningHerleidingQuery = z.infer<typeof verloningHerleidingQuerySchema>

export interface ControleBronLinkDTO {
  /** Leesbare naam van de bron ("Werkbon WB-2026-0412, di 14-04"). */
  label: string
  /** Relatieve backoffice-route naar de bron-editor (deep-link). */
  href: string
}

export interface VerloningHerleidingRegelDTO {
  datum: string | null
  omschrijving: string
  /** Het getal dat deze regel bijdraagt (som = totaal). */
  uren: number
  bron: ControleBronLinkDTO | null
  /** Extra uitleg (bv. "3,00 u reis x 50% = 1,50 u TVT"). */
  toelichting: string | null
}

export interface VerloningHerleidingDTO {
  veld: VerloningHerleidingVeld
  label: string
  /** Het getal waar de lijst onder ligt (uit de controle). */
  totaal: number
  /** De som van de bronregels; gelijk aan `totaal` (anders `sluit = false`). */
  som: number
  sluit: boolean
  regels: VerloningHerleidingRegelDTO[]
}

// ─── Verloning: de berekende weergave per medewerker (ControleSubject.data) ────

export const VERLONING_EMMERS = ['TVT_WERK', 'TVT_REIS', 'ATV'] as const
export type VerloningEmmer = (typeof VERLONING_EMMERS)[number]

export const VERLONING_EMMER_LABELS: Record<VerloningEmmer, string> = {
  TVT_WERK: 'TVT werk',
  TVT_REIS: 'TVT reis',
  ATV: 'ATV',
}

export const VERLONING_KLANTREGEL_SOORTEN = [
  'KLANT',
  'INTERN',
  'OPLEIDING',
  'AFWEZIGHEID',
  'NIET_GEKOPPELD',
] as const
export type VerloningKlantregelSoort = (typeof VERLONING_KLANTREGEL_SOORTEN)[number]

export interface VerloningContractDTO {
  urenPerWeek: number
  /** Contracturen van de maand (FO-318, x 52/12 pro-rata). */
  urenMaand: number
  atv: boolean
  tvtWerk: boolean
  tvtWerkPercentage: number
  tvtReis: boolean
  tvtReisPercentage: number
  /** "38 u/w · 164,67 u/mnd · ATV" */
  label: string
  /** Ingangsdatums (yyyy-MM-dd) van contractmutaties binnen de maand. */
  wijzigtPer: string[]
}

/** Alle maandcijfers ("Alle kolommen", FO-342 §3.2). */
export interface VerloningTotalenDTO {
  /** Uit `aggregatePayrollMonth` (per veld gelijk; regressie-eis). */
  brutoUren: number
  overuren: number
  overschotUren: number
  reistijdUren: number
  reisUrenUitbetaald: number
  verlofUren: number
  ziekUren: number
  toeslagUren: number
  contractUrenPeriode: number
  doorbetaaldeAfwezigheidUren: number
  /** Overige uursoorten: ouderschap + senioren + consignatie-oproep. */
  overigeUursoorten: number
  // Afgeleid uit de bronketen (alleen groeperen):
  gewerkt: number
  teVerlonen: number
  pauzeaftrek: number
  tvtWerkOpbouw: number
  tvtReisOpbouw: number
  tvtMutatie: number
  atvMutatie: number
  correcties: number
  feestdagUren: number
}

export interface VerloningKlantregelDTO {
  /** `KLANT:<klant-id>`, of de soort voor de niet-klantregels (INTERN, OPLEIDING, ...). */
  sleutel: string
  soort: VerloningKlantregelSoort
  naam: string
  gewerkt: number
  naarVerloning: number
  tvtReis: number
  tvtWerk: number
  atv: number
  pauzeaftrek: number
  toeslagUren: number
  /** Per toeslagsoort (tooltip). */
  toeslagPerSoort: Record<string, number>
  correcties: number
  /** Kolommen waar de afrondingsrest op is gezet (tooltip "afronding"). */
  afronding: string[]
}

export interface VerloningSaldoMutatieDTO {
  id: string
  datum: string
  richting: string
  bron: string
  uren: number
  toelichting: string | null
  /** Aangemaakt (UTC-iso) - voor de terugwerkend-signalering. */
  aangemaaktOp: string
  /** De bron-referentie van de boeking (weekovertime:, reisdag:, dayentry:, ...). */
  bronReferentie?: string | null
  /** Doorklik naar de bron van de boeking (weekstaat, werkbon), anders null. */
  bronLink?: ControleBronLinkDTO | null
}

export interface VerloningEmmerSaldoDTO {
  emmer: VerloningEmmer
  begin: number
  eind: number
  /** Som van de mutaties in de periode (eind - begin, behalve bij verval op leesmoment). */
  mutatieTotaal: number
  /** Gegroepeerd per bron (label -> uren). */
  perBron: Record<string, number>
  mutaties: VerloningSaldoMutatieDTO[]
  /** TVT: het plafond (null bij ATV). */
  plafond: number | null
  bovenPlafond: boolean
}

export const TVT_NORM_STATUSSEN = [
  'OK',
  'WAARSCHUWING_LAAG',
  'WAARSCHUWING_HOOG',
  'PLAFOND_BEREIKT',
  'ONVOLDOENDE_HISTORIE',
  'NVT',
] as const
export type TvtNormStatus = (typeof TVT_NORM_STATUSSEN)[number]

export const TVT_NORM_STATUS_LABELS: Record<TvtNormStatus, string> = {
  OK: 'OK',
  WAARSCHUWING_LAAG: 'Waarschuwing: lager dan verwacht',
  WAARSCHUWING_HOOG: 'Waarschuwing: ongebruikelijk hoog',
  PLAFOND_BEREIKT: 'OK, plafond bereikt',
  ONVOLDOENDE_HISTORIE: 'Onvoldoende historie',
  NVT: 'n.v.t.',
}

export interface TvtNormDTO {
  emmer: 'TVT_WERK' | 'TVT_REIS'
  status: TvtNormStatus
  /** null bij n.v.t. en onvoldoende historie (geen norm). */
  verwacht: number | null
  werkelijk: number
  verschil: number | null
  /** Bij n.v.t.: de reden ("verlof", "ziek", "feestdag", "TVT werk niet actief"). */
  reden: string | null
  /** De berekening in gewone taal (tooltip). */
  uitleg: string
}

export interface VerloningDagEntryDTO {
  dayEntryId: string | null
  werkbonId: string
  werkbonNumber: string
  klantNaam: string | null
  uren: number
  pauzeMinuten: number
  /** De pauze die van de betaalde uren afging (0 bij een doorbetaalde pauze). */
  pauzeAftrekMinuten: number
  /** Hoort deze dagregel bij de verloning van de maand (definitieve week, dag in de maand)? */
  inVerloning: boolean
  gepland: { start: string; eind: string }
  geregistreerd: { start: string | null; eind: string | null }
  status: string | null
  afwezig: boolean
}

export interface VerloningDagDTO {
  datum: string
  /** Valt de dag in de urenmaand (bij een week over de maandgrens niet altijd). */
  inMaand: boolean
  gepland: number
  geregistreerd: number
  pauzeMinuten: number
  /** Uitbetaalde reis van de dag zoals de weekstaat hem telt (gesloten dagregels). */
  reisUren: number
  /** Uitbetaalde reis van de dag zoals de maandverloning hem telt (definitieve weken). */
  reisVerloningUren: number
  verlofUren: number
  ziekteUren: number
  feestdagUren: number
  /** Afwijkende TVT-instelling t.o.v. de maandag van de week (gele markering). */
  afwijkendeInstelling: boolean
  entries: VerloningDagEntryDTO[]
}

export interface VerloningWeekDTO {
  isoJaar: number
  isoWeek: number
  /** "2026-W15" */
  sleutel: string
  maandag: string
  zondag: string
  weekstaat: { id: string; status: string } | null
  geregistreerd: number
  verloond: number
  norm: number
  pauzeaftrek: number
  overurenWeekNet: number
  minuren: number
  tvtWerkOpbouw: number
  tvtReisOpbouw: number
  tvtOpname: number
  atvOpbouw: number
  atvOpname: number
  /** De TVT_WERK-opbouw staat op de maandag; deze week telt in de maand van die maandag. */
  opbouwGeboektIn: string
  /** "Opbouw geboekt op ma 31-03 -> telt in maart" bij een week over de maandgrens. */
  maandgrensToelichting: string | null
  instellingen: { tvtWerk: boolean; tvtWerkPercentage: number; tvtReis: boolean; tvtReisPercentage: number; atv: boolean }
  tvtNorm: TvtNormDTO[]
  saldi: Array<{ emmer: VerloningEmmer; begin: number; eind: number; mutaties: VerloningSaldoMutatieDTO[] }>
  dagen: VerloningDagDTO[]
}

export interface VerloningSubjectDataDTO {
  versie: 1
  contract: VerloningContractDTO
  totalen: VerloningTotalenDTO
  perKlant: VerloningKlantregelDTO[]
  perWeek: VerloningWeekDTO[]
  saldi: VerloningEmmerSaldoDTO[]
  uitDienstOp: string | null
  /** De bronregels per klantcel (review fase A, AC7). Ontbreekt bij een oudere berekening. */
  afleiding?: VerloningAfleidingRegelDTO[]
  /** De overige uursoorten los (ouderschap, senioren, consignatie-oproep). */
  overigeDetail?: Array<{ label: string; uren: number }>
}

/** De compacte lijstregel (ControleSubject.samenvatting). */
export interface VerloningSamenvattingDTO {
  contractLabel: string
  gewerkt: number
  teVerlonen: number
  contractUren: number
  tvtMutatie: number
  tvtEind: number
  atvMutatie: number
  atvEind: number
  /** Alle maandcijfers voor de brede tabel en de export. */
  kolommen: VerloningTotalenDTO & {
    tvtWerkBegin: number
    tvtWerkEind: number
    tvtReisBegin: number
    tvtReisEind: number
    atvBegin: number
    atvEind: number
  }
}

// ─── Lijst + tegels + paneel ──────────────────────────────────────────────────

export interface ControleChipDTO {
  regelKey: string
  naam: string
  aantal: number
  ernst: ControleErnst
  /** Kort label voor de chip ("Pauze ontbreekt (2)", "TVT-norm -1,50"). */
  label: string
}

export interface ControleAfhandelingDTO {
  /** OPEN = nog open bevindingen; GECONTROLEERD; GEEN_AFWIJKINGEN. */
  stand: 'OPEN' | 'GECONTROLEERD' | 'GEEN_AFWIJKINGEN'
  doorNaam: string | null
  op: string | null
  aantalGeaccepteerd: number
}

export interface VerloningControleLijstRegelDTO {
  subjectId: string
  employeeId: string
  naam: string
  personeelsnummer: string | null
  bvNaam: string | null
  ernst: ControleErnst
  verouderd: boolean
  fout: string | null
  samenvatting: VerloningSamenvattingDTO | null
  chips: ControleChipDTO[]
  afhandeling: ControleAfhandelingDTO
}

export interface ControleTegelsDTO {
  medewerkers: number
  gecontroleerd: number
  groen: number
  oranje: number
  oranjeBevindingen: number
  rood: number
  roodBevindingen: number
}

export interface ControleRunDTO {
  id: string
  domein: ControleDomein
  jaar: number
  maand: number
  status: ControleRunStatus
  voortgang: number
  aantalSubjects: number
  aantalVerwerkt: number
  gestartOp: string | null
  gereedOp: string | null
  /** Aantal subjects dat sinds de berekening gewijzigd is. */
  aantalVerouderd: number
  aantalFout: number
  fout: string | null
  /** De controle-instellingen zijn gewijzigd sinds de berekening: alles herberekenen. */
  instellingenGewijzigd: boolean
}

export interface ControleAkkoordDTO {
  id: string
  versie: number
  status: ControleAkkoordStatus
  akkoordOp: string
  akkoordDoorNaam: string | null
  openWaarschuwingen: number
  payrollLockId: string | null
  heropendOp: string | null
  heropendDoorNaam: string | null
  heropendReden: string | null
  /** Bewaking: gewijzigd na akkoord (banner). */
  gewijzigdNaAkkoordOp: string | null
  gewijzigdeBronnen: ControleGewijzigdeBronDTO[]
  laatsteBewakingOp: string | null
}

export interface ControleGewijzigdeBronDTO {
  subjectId: string
  naam: string
  /** Wat er wijzigde, in gewone taal ("2 dagregels gewijzigd, 1 saldo-mutatie toegevoegd"). */
  omschrijving: string
}

export interface ControleEindbalkDTO {
  /** "Verloning uren april 2026 → gereed voor akkoord?" */
  kop: string
  medewerkers: number
  gecontroleerd: number
  zonderAfwijkingen: number
  waarschuwingenOpen: number
  waarschuwingenAfgehandeld: number
  blokkerendOpen: number
  teVerlonenUren: number
  tvtMutatieTotaal: number
  atvMutatieTotaal: number
  /** Kan er akkoord gegeven worden, en zo niet: waarom. */
  akkoordMogelijk: boolean
  akkoordBlokkades: string[]
  /** De open waarschuwingen zelf (de eerste 50), voor de akkoorddialoog. */
  openWaarschuwingenLijst?: Array<{ id: string; naam: string; omschrijving: string }>
}

export interface VerloningControleOverzichtDTO {
  jaar: number
  maand: number
  /** "Uren april 2026 -> verloning mei 2026" */
  periodeKop: string
  run: ControleRunDTO | null
  tegels: ControleTegelsDTO
  regels: VerloningControleLijstRegelDTO[]
  /** Aantal regels in de gefilterde set (alle pagina's), voor de paginering. */
  aantalRegels: number
  /** Totaalregel over de GEFILTERDE set (alle pagina's). */
  totaal: VerloningTotalenDTO
  eindbalk: ControleEindbalkDTO
  akkoord: ControleAkkoordDTO | null
  /** Vorige akkoorden (vervangen), nieuwste eerst. */
  akkoordHistorie: ControleAkkoordDTO[]
  gateActief: boolean
}

export interface ControleBevindingDTO {
  id: string
  regelKey: string
  /** De variant binnen de regel (bv. TE_KORT, TE_LANG); null bij een oudere berekening. */
  variant?: string | null
  regelNaam: string
  regelNummer: number
  ernst: ControleErnst
  status: ControleBevindingStatus
  gewijzigdNaControle: boolean
  datum: string | null
  isoWeek: string | null
  omschrijving: string
  bronnen: ControleBronLinkDTO[]
  details: Record<string, unknown> | null
  laatsteActie: { type: ControleActieType; doorNaam: string | null; op: string; reden: string | null } | null
}

export interface ControleActieDTO {
  id: string
  type: ControleActieType
  label: string
  bevindingId: string | null
  bevindingOmschrijving: string | null
  oudeWaarde: unknown
  nieuweWaarde: unknown
  reden: string | null
  doorNaam: string | null
  op: string
}

export const VERLONING_CHECKLIST_TABS = ['KLANTEN', 'WEKEN', 'SALDI', 'AFWIJKINGEN', 'HISTORIE'] as const
export type VerloningChecklistTab = (typeof VERLONING_CHECKLIST_TABS)[number]

export interface ControleChecklistRegelDTO {
  vraag: string
  status: 'OK' | 'WAARSCHUWING' | 'FOUT' | 'INFO'
  zin: string
  tab: VerloningChecklistTab
  /** Optioneel filter op de afwijkingen-tab (regel-sleutels). */
  regelKeys?: string[]
}

export interface VerloningMedewerkerPaneelDTO {
  subjectId: string
  employeeId: string
  naam: string
  personeelsnummer: string | null
  bvNaam: string | null
  periodeKop: string
  ernst: ControleErnst
  verouderd: boolean
  fout: string | null
  berekendOp: string | null
  data: VerloningSubjectDataDTO | null
  checklist: ControleChecklistRegelDTO[]
  bevindingen: ControleBevindingDTO[]
  historie: ControleActieDTO[]
  /** Deep-links naar de bron-editors van deze medewerker. */
  links: { weekstaten: string; verlofMutaties: string; correcties: string; medewerker: string }
  /** Mag de ingelogde gebruiker afhandelen (canEdit) en rood accepteren (canApprove)? */
  rechten: { afhandelen: boolean; roodAccepteren: boolean }
  /** Navigatie in het zijpaneel (vorige/volgende in de huidige sortering). */
  vorigeSubjectId: string | null
  volgendeSubjectId: string | null
}

export interface ControleActieResultaatDTO {
  bevinding: ControleBevindingDTO
  subjectErnst: ControleErnst
}

export interface ControleBulkActieResultaatDTO {
  verwerkt: number
  overgeslagen: Array<{ id: string; reden: string }>
}

// ─── Gate (export / reservering) ──────────────────────────────────────────────

export interface ControleExportVerschilDTO {
  employeeId: string
  naam: string
  veld: string
  akkoord: number
  nu: number
  verschil: number
}

// ─── Stappenbalk (FO-342 §2: 1 Controle → 2 Akkoord → 3 Reservering → 4 NMBRS-export) ──

export const VERLONING_FLOW_STAPPEN = ['CONTROLE', 'AKKOORD', 'RESERVERING', 'EXPORT'] as const
export type VerloningFlowStap = (typeof VERLONING_FLOW_STAPPEN)[number]

export const VERLONING_FLOW_STAP_LABELS: Record<VerloningFlowStap, string> = {
  CONTROLE: 'Controle',
  AKKOORD: 'Akkoord',
  RESERVERING: 'Reservering vastleggen',
  EXPORT: 'NMBRS-export',
}

export interface VerloningFlowStandDTO {
  jaar: number
  maand: number
  periodeKop: string
  gateActief: boolean
  /** De stap waar de maand nu staat; AFGEROND = de NMBRS-export is verwerkt. */
  huidig: VerloningFlowStap | 'AFGEROND'
  run: {
    status: ControleRunStatus
    gereedOp: string | null
    aantalRoodOpen: number
    aantalVerouderd: number
    instellingenGewijzigd: boolean
  } | null
  akkoord: ControleAkkoordDTO | null
  reserveringVastgelegd: boolean
  exportStatus: string | null
}
